import { setValueBindingScreen } from '@common/redux/slice/page';
import { IAppInfo, IPage } from '@common/types/element';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getScreenBinding } from '@common/services';
import { FindScreenBindingResult } from 'click-types';
import { isEmpty, findIndex, debounce } from 'lodash';
import { pullToRefreshSelector } from '@common/redux/selectors/page';
import {
  setDefaultErrorMessages,
  setDefaultValues,
} from '@common/redux/slice/formInputs';
import queryString, { ParsedQuery } from 'query-string';
import { useLocation } from 'react-router-dom';

type Props = {
  screen: IPage;
  dependencies: Record<string, any>;
  appInfo?: Pick<IAppInfo, 'id'>;
};

const filterDatePicker = (metadata: Record<string, any>[]) =>
  metadata.filter(
    (child) =>
      child.type === 'date-picker' &&
      child?.defaultDateValue?.label === 'Current Time'
  );

const convertDateValue = (
  values: Record<string, any>,
  metadata: Record<string, any>[] = []
) => {
  return Object.keys(values).reduce((acc, id) => {
    const index = findIndex(filterDatePicker(metadata), { id: id });
    return {
      ...acc,
      ...(index === -1 && { [id]: values[id] }),
    };
  }, {});
};

const getDefaultDateValue = (bindingResult: FindScreenBindingResult) => {
  const result: Record<string, string> = {};
  Object.entries(bindingResult?.data).forEach(([id, value]: [string, any]) => {
    if (value?.defaultDateValue) {
      result[id] = value?.defaultDateValue;
    }
  });
  return result;
};

type ErrorMessages = {
  mandatory?: string;
};
const getDefaultErrorMessages = (
  values: Record<string, any>
): Record<string, ErrorMessages> => {
  if (typeof values !== 'object') return {};

  const isObjectHasErrorMessage = (obj: any) => {
    if (typeof obj === 'object' && obj?.mandatoryErrorMessage) {
      return true;
    }
    return false;
  };

  const result: Record<string, ErrorMessages> = {};
  const getErrorMessageFromObject = (obj: any) => {
    if (typeof obj !== 'object') {
      return;
    }
    for (const key in obj) {
      if (isObjectHasErrorMessage(obj[key])) {
        if (!result[key]) {
          result[key] = {};
        }
        result[key].mandatory = obj[key]?.mandatoryErrorMessage;
      } else {
        getErrorMessageFromObject(obj[key]);
      }
    }
  };
  getErrorMessageFromObject(values);
  return result;
};

function useBindingScreen({ screen, dependencies, appInfo }: Props): null {
  const dispatch = useDispatch();
  const refreshing = useSelector(pullToRefreshSelector);
  const { search } = useLocation();

  const { targetModal }: ParsedQuery<any> = useMemo(
    () => queryString.parse(search),
    [search]
  );

  const fetBindingPage = useCallback(
    debounce(async (controller: AbortController) => {
      try {
        if (appInfo) {
          const screenBindingResult: FindScreenBindingResult =
            await getScreenBinding({
              appId: appInfo?.id,
              dependencies,
              screenUuid: screen.screenUuid,
              controller,
            });
          if (isEmpty(screenBindingResult?.data)) return;

          dispatch(setDefaultValues(getDefaultDateValue(screenBindingResult)));
          const errors = getDefaultErrorMessages(screenBindingResult.data);
          dispatch(setDefaultErrorMessages(errors));
          dispatch(
            setValueBindingScreen({
              data: convertDateValue(
                screenBindingResult?.data,
                screen.metadata
              ),
              screenUuid: screen.screenUuid,
            })
          );
        }
      } catch (error) {
        if ((error as any)?.message !== 'canceled') {
          console.error('error', error);
        }
      }
    }, 300),
    [
      dependencies?.appInfor?.id,
      dependencies?.currentLocation,
      dependencies?.dimension?.width,
      dependencies?.locale,
      dependencies?.loggedUser,
      dependencies?.routeParam,
      dependencies?.scannerId,
      dependencies?.table,
      dependencies?.timeZone,
      dependencies?.valueInputs,
      screen?.screenUuid,
      appInfo?.id,
      targetModal,
      refreshing,
    ]
  );

  useEffect(() => {
    if (!appInfo) return;
    const controller = new AbortController();
    fetBindingPage(controller);

    return () => {
      controller.abort();
    };
  }, [appInfo?.id, fetBindingPage]);

  return null;
}

export default useBindingScreen;
